import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Bank, CaretDown, ChartLineUp, ClipboardText, Crown, FileText, Gear, Headset, List, Money, Note, Package, Receipt,
  ShieldCheck, ShoppingCart, SignOut, SquaresFour, Users, X,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { API, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  {
    label: "Sales", icon: Receipt, children: [
      { to: "/sales", label: "Invoice", icon: Receipt },
      { to: "/sales?action=create-invoice", label: "Create New Invoice", icon: Receipt, action: "create-invoice" },
      { to: "/estimates", label: "Estimates", icon: Note },
      { to: "/sales?action=payment", label: "Payment Received", icon: Money, action: "payment" },
    ],
  },
  {
    label: "Purchases", icon: ShoppingCart, children: [
      { to: "/purchases", label: "Purchase Bills", icon: ShoppingCart },
      { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardText },
      { to: "/expenses", label: "Expenses", icon: Money },
      { to: "/purchases?action=payment", label: "Payment Made", icon: Money, action: "payment" },
    ],
  },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/banking", label: "Banking", icon: Bank },
  { to: "/parties", label: "Parties", icon: Users },
  { to: "/reports", label: "GST Reports", icon: FileText },
  { to: "/insights", label: "Insights", icon: ChartLineUp },
  { to: "/billing", label: "Subscription", icon: Crown },
  { to: "/support", label: "Support", icon: Headset },
  { to: "/settings", label: "Settings", icon: Gear },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    Sales: location.pathname === "/sales" || location.pathname === "/estimates",
    Purchases: ["/purchases", "/purchase-orders", "/expenses"].includes(location.pathname),
  });
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchId] = useState("");

  useEffect(() => {
    API.get("/branches")
      .then(({ data }) => {
        const branchList = data.branches || [];
        setBranches(branchList);
        const savedId = window.localStorage.getItem("comfy-ledger-branch-id");
        const branch = branchList.find((item) => item.id === savedId) || branchList.find((item) => item.is_default);
        if (!branch) return;
        setActiveBranchId(branch.id);
        if (savedId !== branch.id) {
          window.localStorage.setItem("comfy-ledger-branch-id", branch.id);
          if (savedId) window.location.reload();
        }
      })
      .catch((error) => toast.error(fmtErr(error)));
  }, []);

  const switchBranch = (branchId) => {
    if (branchId === activeBranchId) return;
    window.localStorage.setItem("comfy-ledger-branch-id", branchId);
    window.location.reload();
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
    }`;
  const toggleGroup = (label) => setExpandedGroups((groups) => ({ ...groups, [label]: !groups[label] }));

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center justify-between border-b px-5">
        <span className="font-heading text-lg font-bold tracking-tight text-primary">ADS Billing</span>
        <button className="lg:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-close-button">
          <X size={20} />
        </button>
      </div>
      {branches.length > 0 && (
        <div className="space-y-2 border-b px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Active Branch</p>
          <Select value={activeBranchId} onValueChange={switchBranch}>
            <SelectTrigger data-testid="active-branch-select">
              <SelectValue placeholder="Select branch" />
            </SelectTrigger>
            <SelectContent>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3" data-testid="sidebar-nav">
        {NAV.map((item) => item.children ? (
          <div key={item.label} data-testid={`nav-group-${item.label.toLowerCase()}`}>
            <button
              type="button"
              className={`${linkCls({ isActive: item.children.some((child) => location.pathname === child.to.split("?")[0]) })} w-full justify-between`}
              onClick={() => toggleGroup(item.label)}
              aria-expanded={!!expandedGroups[item.label]}
              data-testid={`nav-${item.label.toLowerCase()}-toggle`}
            >
              <span className="flex items-center gap-3"><item.icon size={18} weight="bold" />{item.label}</span>
              <CaretDown size={16} className={`transition-transform ${expandedGroups[item.label] ? "" : "-rotate-90"}`} />
            </button>
            {expandedGroups[item.label] && (
              <div className="ml-3 mt-1 space-y-1 border-l pl-2">
                {item.children.map((child) => {
                  const [path, query = ""] = child.to.split("?");
                  const expectedSearch = query ? `?${query}` : "";
                  const active = location.pathname === path && location.search === expectedSearch;
                  return (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      className={() => linkCls({ isActive: active })}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    >
                      <child.icon size={16} weight="bold" />
                      {child.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={linkCls}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
          >
            <item.icon size={18} weight="bold" />
            {item.label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <>
            <p className="px-3 pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Administration</p>
            <NavLink to="/admin" className={linkCls} onClick={() => setMobileOpen(false)} data-testid="nav-admin-portal-link">
              <ShieldCheck size={18} weight="bold" />
              Admin Portal
            </NavLink>
          </>
        )}
      </nav>
      <div className="border-t p-4">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              {(user?.name || user?.email || "U")[0].toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{user?.name || "User"}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <button onClick={logout} title="Sign out" data-testid="logout-button" className="text-muted-foreground transition-colors hover:text-destructive">
            <SignOut size={20} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 border-r bg-card lg:block">{sidebar}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r bg-card">{sidebar}</aside>
        </div>
      )}
      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur lg:hidden">
          <button onClick={() => setMobileOpen(true)} data-testid="sidebar-open-button">
            <List size={22} />
          </button>
          <span className="font-heading text-base font-bold text-primary">ADS Billing</span>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
